import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getFallbackManifest } from 'config/discovery-manifest';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import RpcMethod from 'services/rpc/rpcMethod';
import { getRpcParams } from 'services/rpc/rpcUtils';
import { IStore, IStoreAuthentication, IStoreEnvironment } from 'store/types';
import { IDiscoveryManifest, TDiscoveryManifestCollection, TDiscoveryManifestKey } from 'types';
import { decodeAuthToken } from 'utils/authenticationUtils';

export enum DiscoveryManifestErrors {
  DEFINE_ENDPOINT = `Endpoint must be defined`,
  NO_GET_DATA = `GET cannot have 'data', use 'params' instead`,
  MANIFEST_NOT_FOUND = `Requested Manifest not found`,
}

interface IRequestHeaders {
  'key-authorization'?: string;
  [propName: string]: any;
}

export interface IRPCResponseError {
  code: number;
  message: string;
  data: string;
}

export interface IRPCResponse<T> {
  result?: T;
  error?: IRPCResponseError;
  id: string;
  jsonrpc: '2.0';
}

export class DiscoveryManifeset {
  /**
   * static properties
   *
   */
  public static readonly MAX_AGE_SECONDS: number = 60 * 60 * 24; // use 1 day as the cache time
  public static cache: TDiscoveryManifestCollection = Object.assign({}, getFallbackManifest());
  public static updatedOn: DateTime = DateTime.fromMillis(0);

  /**
   * private properties
   *
   */
  private endpoint: IStoreEnvironment['REACT_APP_DISCOVERY_URL'] | undefined;
  private keyAuthorization: IStoreAuthentication['token'] | undefined;

  /**
   * constructor
   *
   */
  constructor() {
    this.resetCache();
  }

  /**
   * public methods
   *
   */

  /**
   * Returns a promise containing the entire discovery manifest collection for the current environment
   */
  public getAll(): Promise<TDiscoveryManifestCollection> {
    if (this.isCacheValid) {
      // console.log('cache hit');
      return Promise.resolve(DiscoveryManifeset.cache);
    } else {
      // console.log('cache miss');
      return this.load();
    }
  }

  /**
   * Returns a promise containing the manifest for the key specified
   */
  public getManifest(key: TDiscoveryManifestKey): Promise<IDiscoveryManifest> {
    return this.getAll().then(
      (collection) => collection[key] || Promise.reject(new Error(DiscoveryManifestErrors.MANIFEST_NOT_FOUND))
    );
  }

  /**
   * Performs an XHR request using the manifest obtained for the key specified
   *
   * @public
   * @async
   * @param key specify which manifest to use
   * @param customConfig optional standard AxiosRequestConfig object to set properties and/or overwrite defaults
   * @param authenticate optionally determines if the key-authorization header will be attached to the request, default is enabled
   *
   * @returns Promise<AxiosResponse<T>>
   *
   * @remarks
   *
   * The method will use parameters provided by the manifest
   *   - URL: IDiscoveryManifest.url
   *   - HTTP method: IDiscoveryManifest.meta.method
   *
   * Foreknowledge is required to know what combination of params and data should be passed in the config object
   *  - for GET pass a params object (request('example', { params: {foo: 'bar'} }))
   *
   *  - for POST pass a data object (request('example', { data: {foo: 'bar'} }))
   *    - if URL variables are required, you can also pass a params object
   *
   */
  public request<T>(
    key: TDiscoveryManifestKey,
    customConfig: AxiosRequestConfig = {},
    authenticate = true
  ): Promise<AxiosResponse<T>> {
    return this.getManifest(key).then((manifest) => {
      const config = this.getRequestConfig(manifest, customConfig, authenticate);

      if (config.method === 'GET' && config.hasOwnProperty('data')) {
        throw new Error(DiscoveryManifestErrors.NO_GET_DATA);
      }

      return axios.request(config);
    });
  }

  /**
   * Invokes an RPC using the manifest obtained for the key specified
   *
   * @param key specify which manifest to use
   * @param config
   * @param config.method RpcMethod to be invoked
   * @param config.params optional data object to be passed to the RPC
   * @param config.requestConfig optional standard AxiosRequestConfig object to set properties and/or overwrite defaults
   * @param config.authenticate optionally determines if the key-authorization header will be attached to the request, default is enabled
   *
   * @returns Promise<AxiosResponse<T>>
   *
   * @remarks
   *
   * The method will parameters provided by the manifest
   *   - URL: IDiscoveryManifest.url
   *   - HTTP method is always POST
   */
  public requestRPC<T>(
    key: TDiscoveryManifestKey,
    config: {
      method: RpcMethod;
      params?: object;
      requestConfig?: AxiosRequestConfig;
      authenticate?: boolean;
    }
  ): Promise<AxiosResponse<IRPCResponse<T>>> {
    const { method, params, requestConfig, authenticate } = _.defaults(config, {
      params: {},
      requestConfig: {},
      authenticate: true,
    });
    const data = getRpcParams(method, params);

    return this.getManifest(key).then((manifest) => {
      const defaultRequestConfig = this.getDefaultRequestConfig(manifest, authenticate);

      const props = _.defaultsDeep(defaultRequestConfig, requestConfig, { data });

      return axios.request(props);
    });
  }

  /**
   * Request utilities
   *
   * Exposed mostly for test access to internals
   *
   */
  public getRequestConfig(manifest: IDiscoveryManifest, customConfig: AxiosRequestConfig, authenticate = true) {
    const defaultConfig = this.getDefaultRequestConfig(manifest, authenticate);

    return _.defaultsDeep(customConfig, defaultConfig);
  }

  public getDefaultRequestConfig(manifest: IDiscoveryManifest, authenticate = true) {
    const {
      url,
      meta: { method },
    } = manifest;

    return {
      headers: this.getDefaultHeaders(authenticate),
      method,
      url,
      params: this.getDefaultRequestParams(),
    };
  }

  public getDefaultRequestParams() {
    return {
      _version: '3.2',
      _debug: true,
    };
  }

  public getDefaultHeaders(authenticate = true) {
    const headers: IRequestHeaders = {};

    if (authenticate && this.keyAuthorization) {
      headers['key-authorization'] = this.keyAuthorization;
    }

    return headers;
  }

  public setConfig(environment: IStoreEnvironment, authentication: IStoreAuthentication) {
    const { REACT_APP_DISCOVERY_URL } = environment;
    const { token } = authentication;

    this.setEndpoint(REACT_APP_DISCOVERY_URL);
    this.setKeyAuthorization(token);
  }

  public setEndpoint(REACT_APP_DISCOVERY_URL: IStoreEnvironment['REACT_APP_DISCOVERY_URL']) {
    this.endpoint = REACT_APP_DISCOVERY_URL;
  }

  public setKeyAuthorization(token: IStoreAuthentication['token']) {
    this.keyAuthorization = decodeAuthToken(token || '').keyAuthorization;
  }

  public resetCache() {
    DiscoveryManifeset.cache = Object.assign({}, getFallbackManifest());
    DiscoveryManifeset.updatedOn = DateTime.fromMillis(0);
  }

  public get isCacheValid(): boolean {
    const isCached = this.isValidCollection(DiscoveryManifeset.cache);
    const isCurrent = !this.isCacheExpired;

    return isCached && isCurrent;
  }

  public get isCacheExpired() {
    const now = DateTime.utc();
    const expiration = DiscoveryManifeset.updatedOn.plus({
      seconds: DiscoveryManifeset.MAX_AGE_SECONDS,
    });
    const isExpired = now > expiration;

    return isExpired;
  }

  /**
   * private methods
   *
   */
  private load() {
    if (this.endpoint === undefined) {
      throw new Error(DiscoveryManifestErrors.DEFINE_ENDPOINT);
    }

    return axios
      .get<TDiscoveryManifestCollection>(this.endpoint)
      .then((response) => {
        const { data } = response;

        if (this.isValidCollection(data)) {
          DiscoveryManifeset.cache = Object.assign({}, data);
          DiscoveryManifeset.updatedOn = DateTime.utc();
        }

        return DiscoveryManifeset.cache;
      })
      .catch((e) => {
        if (process.env.NODE_ENV === 'development') {
          // tslint:disable-next-line: no-console
          console.error(`Manifest Load Error: ${e}`);
        }

        return DiscoveryManifeset.cache;
      });
  }

  private isValidCollection(manifest: any): boolean {
    return !_.isNil(manifest) && !_.isEmpty(manifest);
  }
}

// this is a singleton so that the cache is shared across hook instances
const manifestClient = new DiscoveryManifeset();

function useDiscoveryManifest() {
  const environment = useSelector<IStore, IStoreEnvironment>((state) => state.environment);
  const authentication = useSelector<IStore, IStoreAuthentication>((state) => state.authentication);

  manifestClient.setConfig(environment, authentication);

  return manifestClient;
}

export default useDiscoveryManifest;
